import { z } from "zod";
/**
 * «Боты» (Паша 03.10.2026, 04.10.2026 «переизобрети SaleBot»): the owner's and the marketer's control panel of the
 * Academy bot. The Mini App keeps the bot's ledger (hypno-practicum-miniapp lib/bot-ledger.mts, loopback ops API
 * 127.0.0.1:9940): the bot base and who can be messaged, the branches of the bot with where each person is now
 * (going / done / out by an event / unreachable — adding up to «были в ветке»), entries, every message as a step,
 * campaigns, overlaps of branches and the checks of the bot's rules. Every number carries the key of its people
 * (`set`); Vekha lists them on a click with a link to the person's amo deal. Vekha adds its own messages (memo,
 * reminders, Yana's first contact) to the branches they belong to. Bot messages switch on and off here; Vekha's texts
 * are edited on «Автосообщения»; campaigns stay with Аркадий.
 */
export const BOT_BRANCH_KEYS = ["practicum", "webinar_registration", "webinar_application", "booking"];
export const BotBranchKeySchema = z.enum(BOT_BRANCH_KEYS);
const Moment = z.string().min(1).max(64).nullable();
const Int = z.number().int().min(0);
const Text = (max) => z.string().max(max);
/** A number with the key of its people. */
export const BotCountSchema = z.object({ count: Int, set: z.string().min(1).max(200) }).strict();
const Counted = (shape) => z.object({ ...shape, count: Int, set: z.string().min(1).max(200) });
/* ---------------------------------------------------------------- Mini App ops API: GET /api/ops/bot-ledger */
export const BotStepStatsSchema = z.object({
    sent: Int, delivered: Int, failed: Int, sent7d: Int, delivered7d: Int, failed7d: Int, clicks: Int, lastSentAt: Moment,
});
export const BotLedgerStepSchema = z.object({
    id: z.string().min(1).max(200),
    title: Text(300),
    trigger: Text(600),
    delay: Text(300),
    text: Text(8_000),
    buttons: z.array(Text(300)).max(50),
    /** null: no switch (the care notices). */
    enabled: z.boolean().nullable(),
    switchable: z.boolean(),
    sender: z.literal("academy_bot"),
    stats: BotStepStatsSchema,
    received: BotCountSchema, waiting: BotCountSchema, blockedAfter: BotCountSchema, clicked: BotCountSchema, canSend: BotCountSchema,
    /** The ready request to Аркадий for this step. */
    phrase: Text(1_000),
});
export const BotLedgerStageSchema = z.object({
    key: Text(60), title: Text(200), hint: Text(1_000),
    /** null: the stage has no people of its own (the access messages run over several stages). */
    people: BotCountSchema.nullable(), canSend: BotCountSchema.nullable(), canSendClean: BotCountSchema.nullable(),
    phrase: Text(1_000).nullable(),
    steps: z.array(Text(200)).max(100),
});
export const BotLedgerBranchSchema = z.object({
    key: BotBranchKeySchema, title: Text(200), hint: Text(1_000),
    were: BotCountSchema,
    statuses: z.array(Counted({ key: z.enum(["going", "done", "exited", "unreachable"]), title: Text(200), hint: Text(1_000) })).length(4),
    exits: z.array(Counted({ key: Text(60), title: Text(200) })).max(30),
    unreachable: z.array(Counted({ key: Text(60), title: Text(200) })).max(30),
    entries: z.array(Counted({ key: Text(120), title: Text(300), hint: Text(1_000) })).max(100),
    canSend: BotCountSchema, canSendClean: BotCountSchema,
    phrase: Text(1_000),
    stages: z.array(BotLedgerStageSchema).max(30).nullable(),
    steps: z.array(BotLedgerStepSchema).max(200),
});
export const BotLedgerCheckSchema = Counted({
    key: Text(60), title: Text(300), hint: Text(1_000), whose: Text(200), since: Moment,
    status: z.enum(["ok", "problem", "info"]),
});
export const OpsBotLedgerSchema = z.object({
    checkedAt: Text(64),
    /** The analytics core's data moment (booked, held, bought by the person card), or null when it did not answer. */
    coreAsOf: Moment,
    calendar: z.enum(["ok", "unavailable"]),
    base: z.object({
        total: BotCountSchema, canSend: BotCountSchema, canSendClean: BotCountSchema, tests: Int,
        reach: z.array(Counted({ key: Text(60), title: Text(200), hint: Text(1_000) })).max(20),
    }),
    branches: z.array(BotLedgerBranchSchema).max(10),
    common: z.array(BotLedgerStepSchema).max(50),
    campaigns: z.array(z.object({ id: Text(100), name: Text(300), createdAt: Text(64), sent: Int, delivered: Int, failed: Int,
        received: BotCountSchema, blockedAfter: BotCountSchema })).max(500),
    blockedWithoutMessage: BotCountSchema,
    overlaps: z.object({ two: BotCountSchema, three: BotCountSchema,
        pairs: z.array(Counted({ keys: z.array(BotBranchKeySchema).length(2), title: Text(300) })).max(20) }),
    checks: z.array(BotLedgerCheckSchema).max(50),
    uncomputable: z.array(Text(1_000)).max(30),
});
/** `GET /api/ops/bot-ledger/people?set=…&q=…`: at most 200 people of one number, newest first. */
export const BotPersonRowSchema = z.object({
    telegramId: z.string().regex(/^\d{1,20}$/u),
    name: Text(300).nullable(),
    username: Text(100).nullable(),
    /** What about this person makes them part of the number: «получил 04.10, 21:05», «уйдёт 05.10, 09:00», why a check fails. */
    line: Text(1_000),
    at: Moment,
});
export const OpsBotLedgerPeopleSchema = z.object({ set: Text(200), total: Int, matched: Int, people: z.array(BotPersonRowSchema).max(200) });
/**
 * `POST /api/calendar/customer/bot-bookings` (signed, the Mini App): the calendar bookings «Боты» and the bot's person
 * facts need — upcoming ones and those of the last 30 days, without test bookings. `cardTelegramIds`: the Telegram IDs
 * on the amo contact cards of the booking's deal (fields 630983 and 627751); the booking's own `telegramUserId` first.
 */
export const BotCalendarBookingSchema = z.object({
    bookingId: z.string().uuid(),
    origin: z.enum(["leader", "manager", "yulia", "miniapp", "yana"]),
    status: z.enum(["upcoming", "held", "cancelled", "other"]),
    startsAt: z.string().min(1).max(64),
    createdAt: z.string().min(1).max(64),
    personKey: z.string().min(1).max(260),
    telegramUserId: z.string().regex(/^[1-9]\d{0,19}$/u).nullable(),
    cardTelegramIds: z.array(z.string().regex(/^[1-9]\d{0,19}$/u)).max(20),
}).strict();
export const BotCalendarBookingsSchema = z.object({ checkedAt: z.string().min(1).max(64), sinceDays: z.number().int().min(1).max(90), bookings: z.array(BotCalendarBookingSchema).max(5_000) }).strict();
/* ---------------------------------------------------------------- What Vekha serves to the page */
/** Who sends the message: the Academy bot in Telegram, or Vekha (Yana / the care department). */
export const BOT_SENDERS = ["academy_bot", "vekha"];
export const BotSenderSchema = z.enum(BOT_SENDERS);
/** Vekha's own auto message in a branch: its text from «Автосообщения» and its 7-day counters. */
export const BotFunnelMessageSchema = z.object({
    id: z.string().min(1).max(200),
    title: z.string().min(1).max(300),
    trigger: z.string().max(600).nullable(),
    delay: z.string().max(300).nullable(),
    text: z.string().max(8_000),
    buttons: z.array(z.string().max(300)).max(50),
    sender: BotSenderSchema,
    enabled: z.boolean().nullable(),
    sent7d: Int.nullable(),
    delivered7d: Int.nullable(),
    failed7d: Int.nullable(),
    lastSentAt: Moment,
    /** What the numbers mean when they are not plain sends, or why there are none. */
    statsNote: z.string().max(300).nullable(),
}).strict();
/** ok — the ops API answered; unavailable — it did not (timeout, 5xx, 404); not_configured — no token. */
export const BOT_SOURCE_STATES = ["ok", "unavailable", "not_configured"];
export const BotSourceStateSchema = z.enum(BOT_SOURCE_STATES);
export const BotsOverviewSchema = z.object({
    checkedAt: z.string().min(1).max(64),
    source: BotSourceStateSchema,
    /** The bot's ledger, or null when the bot did not answer (the page says so and shows Vekha's messages). */
    ledger: OpsBotLedgerSchema.nullable(),
    /** Vekha's own messages by branch, in the order a person meets them. */
    vekha: z.object({ booking: z.array(BotFunnelMessageSchema).max(50), webinar_application: z.array(BotFunnelMessageSchema).max(50) }),
    /** Whether this owner may switch bot messages: the ops API is reachable and Vekha knows their Telegram ID. */
    canSwitch: z.boolean(),
    switchBlockedReason: z.string().max(300).nullable(),
}).strict();
/** `GET /api/bots/people?set=…&q=…`: the people of one number with a link to their amo deal (the open one first). */
export const BotPeopleListSchema = z.object({
    set: z.string().max(200), total: Int, matched: Int,
    people: z.array(BotPersonRowSchema.extend({ dealUrl: z.string().url().max(300).nullable(), dealClosed: z.boolean().nullable() })).max(200),
}).strict();
export const BotPeopleQuerySchema = z.object({
    set: z.string().min(1).max(200).regex(/^[\w.:+-]+$/u),
    q: z.string().max(100).optional(),
}).strict();
export const BotMessageSwitchSchema = z.object({ enabled: z.boolean() }).strict();
export const BotMessageSwitchResultSchema = z.object({ id: z.string().min(1).max(200), enabled: z.boolean() }).strict();
//# sourceMappingURL=bots.js.map